import { Close } from '@/components/icons'
import { ROUTE_CLUBS, ROUTE_COACHES } from '@/constants/Routes'
import { serverErrorMessage } from '@/services/httpService'
import {
  BenefitTarget,
  BenefitTargetCoach,
  CLUB_TARGET_KIND,
  ClaimPolicyView,
  HeldBenefit,
  clubTargetUrn,
  clubTargetUrnPrefix,
  useBenefitCoachTargets,
  useBenefitTargets,
  useClaimBenefit,
} from '@/services/myBenefits'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'usehooks-ts'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ClaimOption, claimOptions } from '@/pages/benefits/claimOptions'

/**
 * Every optional is `| null` and required at the call site rather than omissible.
 *
 * These are not decorations: `fixedTarget` decides whether a picker is shown at all, and
 * `onSelectTarget` decides who owns the selection. Left omissible, each caller silently got
 * whichever behavior `undefined` happened to produce, and the difference between "no fixed target"
 * and "forgot to pass one" was invisible at both ends.
 */
type Props = {
  open: boolean
  onClose: () => void
  benefit: HeldBenefit | null
  /** Pre-selects a target and skips the lists — used by the claim button on a club or coach page. */
  fixedTarget: BenefitTarget | null
  /** Told about a successful claim. Null when the caller has nothing to do afterwards. */
  onClaimed: ((target: BenefitTarget) => void) | null
  /**
   * Selection lifted to the caller so it can outlive this component, carried as the target's URN
   * because that is the one identifier both kinds share. A member deciding between coaches will
   * open one to read about them, and coming back must not silently drop what they had chosen.
   *
   * `onSelectTarget` null means uncontrolled — the modal keeps its own selection, which is right
   * for the fixed-target variant where there is nothing to choose. It, not `selectedTargetUrn`, is
   * what decides: a controlled caller with nothing selected yet passes a null URN too, so the URN
   * alone cannot tell the two apart.
   */
  selectedTargetUrn: string | null
  onSelectTarget: ((urn: string | null) => void) | null
}

/**
 * How a benefit's claim policy is fetched.
 *
 * The kinds themselves come from the benefit — this only says which endpoint answers for a kind,
 * which is a fact about the API rather than about the benefit: clubs are indexed and paged
 * separately from platform entities, so the server offers two listings. Everything a member reads
 * — which kinds exist, what each is called, what it is worth — comes off `claimPolicies`.
 */
const isClubPolicy = (policy: ClaimPolicyView) => policy.targetKind === CLUB_TARGET_KIND

/**
 * Choosing where to claim a benefit.
 *
 * Claiming is final from the member's side — the target owns it afterwards and DUPR will not move
 * it — so this is deliberately three steps and not one: pick, read back what you picked, confirm.
 * The middle step exists because the sentence it shows ("this can't be moved later") is the only
 * thing standing between a mis-tap and an unrecoverable choice.
 *
 * ## Why tabs rather than one list
 *
 * A benefit says where it can be spent — clubs, coaches, or both, and for a bundle made of several
 * benefits, which part — and each of those is a tab (see `claimOptions`). The listings stay separate
 * because they are paged by different server-side orderings (own-clubs-first over the club index,
 * distance over the player index) and merging them would interleave two orders that only make
 * sense within themselves.
 *
 * The order within each list is the server's and is deliberately not re-sorted here: it spans
 * pages, so sorting a page would only shuffle whichever rows happened to arrive together.
 */
export const BenefitClaimModal = ({
  open,
  onClose,
  benefit,
  fixedTarget,
  onClaimed,
  selectedTargetUrn,
  onSelectTarget,
}: Props) => {
  const { t } = useTranslation()
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null)
  const [ownSelectedUrn, setOwnSelectedUrn] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  // The key of the tab the member picked; null until they pick one. See `activeOption`.
  const [kind, setKind] = useState<string | null>(null)

  const controlled = onSelectTarget !== null
  const activeSelectedUrn = controlled ? selectedTargetUrn : ownSelectedUrn
  const select = (urn: string | null) => (controlled ? onSelectTarget?.(urn) : setOwnSelectedUrn(urn))

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce<string>(search, 250)

  const claim = useClaimBenefit()
  const picking = open && !fixedTarget && benefit !== null

  /**
   * The ways this benefit can be spent, which ARE the tabs — see `claimOptions`.
   *
   * For a single benefit, one per kind of place it names. For a bundle made of several benefits,
   * one per claimable part (and kind, when a part can go to more than one): the bundle is never
   * claimed itself, so each tab carries its part's own grant, and the active tab decides both which
   * listing is asked and which grant it is asked for and claimed with. Nothing here supplies a kind
   * the benefit did not name: with no option there is nothing to list and nothing to claim.
   */
  const options = useMemo(() => (benefit ? claimOptions(benefit) : []), [benefit])
  // The member's tab if they picked one; otherwise the tab the current selection came from (Back from
  // a club's or coach's page lands here with it in the URL), otherwise the first.
  const activeOption: ClaimOption | null =
    options.find((option) => option.key === kind) ??
    (activeSelectedUrn
      ? options.find((option) => isClubPolicy(option.policy) === activeSelectedUrn.startsWith(clubTargetUrnPrefix))
      : undefined) ??
    options[0] ??
    null
  const activePolicy: ClaimPolicyView | null = activeOption?.policy ?? null
  const showingClubs = activePolicy !== null && isClubPolicy(activePolicy)
  const listedGrantId = picking && activeOption ? activeOption.grantId : null

  // Only the active tab's listing is asked: each tab can be a different grant, so a listing nobody
  // is looking at would be asked for the wrong one.
  const clubQuery = useBenefitTargets(listedGrantId, position, debouncedSearch, showingClubs)
  const coachQuery = useBenefitCoachTargets(
    listedGrantId,
    position,
    debouncedSearch,
    showingClubs ? '' : (activePolicy?.targetKind ?? ''),
    activeOption !== null && !showingClubs
  )

  // Flattened in arrival order. The server's ordering spans pages, so concatenating is the only
  // thing that preserves it; re-sorting here would shuffle whichever rows arrived together.
  const clubs = useMemo(
    () => (showingClubs ? (clubQuery.data?.pages.flatMap((page) => page.clubs) ?? []) : []),
    [clubQuery.data, showingClubs]
  )
  const coaches = useMemo(
    () => (!showingClubs && activeOption ? (coachQuery.data?.pages.flatMap((page) => page.coaches) ?? []) : []),
    [coachQuery.data, showingClubs, activeOption]
  )

  const activeQuery = showingClubs ? clubQuery : coachQuery
  // A disabled query reports `isLoading` in React Query v4, so only an asked-for list may speak.
  const loading = activeOption !== null && activeQuery.isLoading
  const failed = activeOption !== null && activeQuery.isError

  useEffect(() => {
    if (!picking) return
    // Distance ordering is a nicety, not a requirement — a refused prompt still yields the full
    // participating list, just unordered. Never block a claim on a permission dialog.
    navigator.geolocation?.getCurrentPosition(
      (p) => setPosition({ lat: p.coords.latitude, lng: p.coords.longitude }),
      // Refused or unavailable: stay at null. The list is then unordered rather than absent, which
      // is the trade this makes on purpose.
      () => setPosition(null),
      { timeout: 5000 }
    )
  }, [picking])

  useEffect(() => {
    if (!open) {
      setError(null)
      setSearch('')
      setConfirming(false)
      setKind(null)
    }
  }, [open])

  if (!benefit) return null

  const label = benefit.label ?? t('benefits.aBenefit')

  const selectedCoach = coaches.find((coach) => coach.targetUrn === activeSelectedUrn) ?? null
  const selectedClub = clubs.find((club) => clubTargetUrn(club.clubId) === activeSelectedUrn) ?? null
  const chosen: BenefitTarget | null =
    fixedTarget ??
    (selectedCoach
      ? { kind: 'coach', id: selectedCoach.playerId, name: selectedCoach.name, urn: selectedCoach.targetUrn }
      : selectedClub
        ? {
            kind: 'club',
            id: selectedClub.clubId,
            name: selectedClub.clubName,
            urn: clubTargetUrn(selectedClub.clubId),
          }
        : null)

  // A fixed target claims the benefit the caller handed over; a picked one, the active tab's grant —
  // for a bundle, the part being claimed.
  const claimGrantId = fixedTarget ? benefit.grantId : (activeOption?.grantId ?? null)

  const onConfirm = () => {
    if (!chosen || claimGrantId === null) return
    setError(null)
    claim.mutate(
      { grantId: claimGrantId, target: chosen },
      {
        onSuccess: () => {
          onClaimed?.(chosen)
          onClose()
        },
        // The server's own wording where it has any. A refused claim usually has a reason worth
        // reading — "This benefit is not claimable" after claiming it in another tab, or a target
        // rule declining it — and the generic string threw all of them away, leaving a member to
        // press the same button again against an answer that will not change.
        onError: (claimError) => setError(serverErrorMessage(claimError, t('benefits.claimFailed'))),
      }
    )
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {confirming && chosen
                        ? t('benefits.confirmTitle', { label, targetName: chosen.name })
                        : t('benefits.claimTitle', { label })}
                    </Dialog.Title>
                    <p className="mt-0.5 text-sm text-gray-500">
                      {confirming
                        ? t('benefits.confirmDesc')
                        : fixedTarget
                          ? t('benefits.claimAtTargetDesc', { targetName: fixedTarget.name })
                          : t('benefits.claimPickDesc')}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    aria-label={t('benefits.close')}
                    className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Close />
                  </button>
                </div>

                {!confirming && !fixedTarget && (
                  <>
                    {/* Only shown when there is genuinely a choice of kind. One tab is not a tab,
                        it is a label that does nothing. */}
                    {options.length > 1 && (
                      <div
                        role="tablist"
                        aria-label={t('benefits.targetKindLabel')}
                        className="mt-3 flex gap-1 rounded-md bg-gray-100 p-1"
                      >
                        {options.map((option) => (
                          <button
                            key={option.key}
                            role="tab"
                            aria-selected={activeOption?.key === option.key}
                            className={`flex-1 rounded px-3 py-1.5 text-sm transition-colors ${
                              activeOption?.key === option.key
                                ? 'bg-white font-medium text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => setKind(option.key)}
                          >
                            {/* The server's own names — for the kind of place, or for a bundle's
                                part. Nothing here knows what a club or a coach is, so a benefit
                                that names a third kind gets a working tab with no change. */}
                            {option.namesKind
                              ? t('benefits.partAtKind', { label: option.label, kind: option.policy.targetKindLabel })
                              : option.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Nowhere this benefit names: nothing to search and nothing to claim. The lists
                        never pick a kind of place on its behalf. */}
                    {activeOption === null && (
                      <p className="py-6 text-center text-sm text-gray-500">{t('benefits.nothingToClaim')}</p>
                    )}

                    {activeOption !== null && (
                      <>
                        <input
                          className="mt-3 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                          placeholder={showingClubs ? t('benefits.searchClubs') : t('benefits.searchCoaches')}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />

                        <div className="mt-3 max-h-72 overflow-y-auto">
                          {loading && <p className="py-6 text-center text-sm text-gray-400">{t('benefits.loading')}</p>}

                          {/* Checked before the empty state, and never merged with it. "Nowhere near
                          you is taking this" is an answer about the world; a failed request is no
                          answer at all. Showing the first when we mean the second tells a member
                          their benefit is worthless because a request timed out. */}
                          {!loading && failed && (
                            <div className="py-6 text-center">
                              <p className="text-sm font-medium text-gray-700">{t('benefits.targetsFailedTitle')}</p>
                              <p className="mt-1 text-xs text-gray-500">{t('benefits.targetsFailedDesc')}</p>
                              <button
                                className="mt-3 text-xs text-gray-900 underline"
                                onClick={() => activeQuery.refetch()}
                              >
                                {t('benefits.tryAgain')}
                              </button>
                            </div>
                          )}

                          {/* Nowhere to claim: the active tab's list came back empty. */}
                          {!loading && !failed && clubs.length === 0 && coaches.length === 0 && (
                            <div className="py-6 text-center">
                              <p className="text-sm font-medium text-gray-700">{t('benefits.noTargetsTitle')}</p>
                              <p className="mt-1 text-xs text-gray-500">{t('benefits.noTargetsDesc')}</p>
                            </div>
                          )}

                          <ul className="divide-y divide-gray-100">
                            {showingClubs &&
                              clubs.map((club) => {
                                const urn = clubTargetUrn(club.clubId)
                                return (
                                  <TargetRow
                                    key={urn}
                                    name={club.clubName}
                                    detail={club.shortAddress}
                                    // Every participating club honors the same program on the same
                                    // terms, so the program's own name is the most specific true
                                    // thing there is to say about this row.
                                    // What a claim at this KIND resolves to, per the policy — not the
                                    // bundle's own name, which says nothing about what it is worth
                                    // here.
                                    worth={activePolicy?.label ?? activeOption?.benefit.label ?? null}
                                    imageUrl={club.mediaUrl}
                                    selected={urn === activeSelectedUrn}
                                    onSelect={() => select(urn === activeSelectedUrn ? null : urn)}
                                    viewHref={`${ROUTE_CLUBS}/${club.clubId}`}
                                    viewLabel={t('benefits.viewClub')}
                                  />
                                )
                              })}

                            {!showingClubs &&
                              coaches.map((coach) => (
                                <TargetRow
                                  key={coach.targetUrn}
                                  name={coach.name}
                                  detail={coach.headline ?? coach.shortAddress}
                                  // A coach settles the band themselves, so this genuinely differs row
                                  // to row — it is the reason the coach list shows terms and the club
                                  // list cannot.
                                  worth={coach.worth ?? activePolicy?.label ?? null}
                                  imageUrl={coach.imageUrl}
                                  selected={coach.targetUrn === activeSelectedUrn}
                                  onSelect={() =>
                                    select(coach.targetUrn === activeSelectedUrn ? null : coach.targetUrn)
                                  }
                                  viewHref={coachProfileHref(coach)}
                                  viewLabel={t('benefits.viewCoach')}
                                />
                              ))}
                          </ul>

                          {activeQuery.hasNextPage && (
                            // Explicit rather than infinite-scroll: the list lives in a short scroll
                            // box inside a dialog, where a scroll sentinel fires unpredictably.
                            <button
                              className="mt-2 w-full py-2 text-xs text-gray-600 underline disabled:opacity-40"
                              disabled={activeQuery.isFetchingNextPage}
                              onClick={() => activeQuery.fetchNextPage()}
                            >
                              {activeQuery.isFetchingNextPage
                                ? t('benefits.loading')
                                : showingClubs
                                  ? t('benefits.loadMoreClubs')
                                  : t('benefits.loadMoreCoaches')}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {confirming && chosen && (
                  <div className="mt-4 rounded-md bg-gray-50 p-3">
                    <p className="text-sm text-gray-700">
                      {chosen.kind === 'coach'
                        ? t('benefits.confirmCoachBody', { targetName: chosen.name })
                        : t('benefits.confirmClubBody', { targetName: chosen.name })}
                    </p>
                  </div>
                )}

                {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

                {/* Stated while the choice is still open, not after it is made. */}
                {!confirming && <p className="mt-4 text-xs text-gray-400">{t('benefits.claimIsFinal')}</p>}

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => (confirming ? setConfirming(false) : onClose())}
                  >
                    {confirming ? t('benefits.chooseSomewhereElse') : t('common.cancel')}
                  </button>
                  <button
                    className="rounded-full bg-[#3A79E0] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2D5FB8] disabled:opacity-40"
                    disabled={!chosen || claimGrantId === null || claim.isLoading}
                    onClick={() => (confirming ? onConfirm() : setConfirming(true))}
                  >
                    {confirming
                      ? t('benefits.confirmCta')
                      : chosen
                        ? t('benefits.claimAt', { targetName: chosen.name })
                        : t('benefits.claimCtaDisabled')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

/**
 * A coach's profile URL, built from the entity URN the server sent.
 *
 * The URN is the only place the entity's own id appears — `playerId` names the person operating the
 * profile, which is a different id in a different space — so the last segment is taken from it
 * rather than guessed at.
 */
const coachProfileHref = (coach: BenefitTargetCoach): string => {
  const entityId = coach.targetUrn.split(':').pop()
  return `${ROUTE_COACHES}/${coach.entityKind}/${entityId}`
}

/**
 * One selectable target.
 *
 * Selecting and committing are separate steps, so the row only selects — nothing here spends the
 * benefit. The "view" affordance is a real link rather than a handler so the browser's own Back
 * returns to the picker with the selection still in the URL.
 */
const TargetRow = ({
  name,
  detail,
  worth,
  imageUrl,
  selected,
  onSelect,
  viewHref,
  viewLabel,
}: {
  name: string
  detail: string | null
  worth: string | null
  imageUrl: string | null
  selected: boolean
  onSelect: () => void
  viewHref: string
  viewLabel: string
}) => (
  <li className="flex items-center gap-2">
    <button
      className={`flex flex-1 items-center gap-3 px-1 py-2.5 text-left transition-colors ${
        selected ? 'bg-gray-50' : 'hover:bg-gray-50'
      }`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {imageUrl && <img src={imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{name}</span>
        {detail && <span className="block truncate text-xs text-gray-500">{detail}</span>}
      </span>
      {worth && (
        // Capped and truncated rather than left to grow: the label is authored by whoever defined
        // the program, and one long enough to wrap would squeeze the name beside it — which is
        // the thing the row exists to let someone read.
        <span
          className="max-w-[9rem] shrink-0 truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
          title={worth}
        >
          {worth}
        </span>
      )}
    </button>

    <Link to={viewHref} className="shrink-0 px-2 py-1 text-xs text-gray-500 underline hover:text-gray-900">
      {viewLabel}
    </Link>
  </li>
)
